// Klocki pakietu z Pola.tsx (osobny plik - limit 300 linii; import dalej przez ./Pola): karty wyboru, krokomierz,
// pole na zdjęcia.
import { useId } from "react";
import { cx } from "./klasy";
import { IkonaMinus, IkonaPlus } from "./ikony";
import type { KomponentIkony } from "./ikony";
import type { LiniaProduktu } from "./Okno";
import "../styles/wspolne.css";

export interface KartaWyboruOpcja<W extends string = string> {
  wartosc: W;
  tytul: string;
  opis?: string;
  ikona?: KomponentIkony;
  wylaczona?: boolean;
  /** Kolor linii produktu (ikona i ramka wyboru) - np. kreator: kolumny i T-4 turkusowe */
  linia?: LiniaProduktu;
}

export interface KartyWyboruProps<W extends string> {
  legenda: string;
  ukryjLegende?: boolean;
  wartosc: W | null;
  opcje: ReadonlyArray<KartaWyboruOpcja<W>>;
  onZmien: (wartosc: W) => void;
  /** Ikona nad tekstem, wyśrodkowane - dla 3-4 krótkich opcji w rzędzie */
  pionowe?: boolean;
  className?: string;
}

/** Wybór jednej opcji w postaci dużych kart (radio). */
export function KartyWyboru<W extends string>({ legenda, ukryjLegende, wartosc, opcje, onZmien, pionowe, className }: KartyWyboruProps<W>) {
  const nazwa = useId();
  return (
    <fieldset className="iot-pole">
      <legend className={cx("iot-pole__etykieta", ukryjLegende && "iot-sr")}>{legenda}</legend>
      <div className={cx("iot-karty-wyboru", pionowe && "iot-karty-wyboru--pionowe", className)}>
        {opcje.map(({ wartosc: w, tytul, opis, ikona: Ikona, wylaczona, linia }) => (
          <label key={w} className="iot-karta-wyboru" data-iot-linia={linia}>
            <input type="radio" className="iot-karta-wyboru__input" name={nazwa} value={w} checked={wartosc === w} disabled={wylaczona} onChange={() => onZmien(w)} />
            {Ikona && <Ikona className="iot-karta-wyboru__ikona" />}
            <span>
              <span className="iot-karta-wyboru__tytul">{tytul}</span>
              {opis && <span className="iot-karta-wyboru__opis">{opis}</span>}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export interface KrokomierzProps {
  etykieta: string;
  /** Wartość już sformatowana do wyświetlenia, np. "72,0" */
  wartosc: string;
  onZmien: (wartosc: string) => void;
  onMniej: () => void;
  onWiecej: () => void;
  /** Tekst przycisków dla czytników, np. "Mniej o 0,5 stopnia" */
  etykietaMniej: string;
  etykietaWiecej: string;
  mniejWylaczone?: boolean;
  wiecejWylaczone?: boolean;
  maly?: boolean;
  jednostka?: string;
}

/** Duży licznik -/+ do ustawiania celu temperatury, kilogramów itp. */
export function Krokomierz({ etykieta, wartosc, onZmien, onMniej, onWiecej, etykietaMniej, etykietaWiecej, mniejWylaczone, wiecejWylaczone, maly, jednostka }: KrokomierzProps) {
  const id = useId();
  return (
    <div className="iot-wiersz">
      <label htmlFor={id} className="iot-sr">{etykieta}</label>
      <div className={cx("iot-krokomierz", maly && "iot-krokomierz--maly")}>
        <button type="button" className="iot-krokomierz__przycisk" aria-label={etykietaMniej} onClick={onMniej} disabled={mniejWylaczone}><IkonaMinus /></button>
        <input id={id} className="iot-krokomierz__wartosc" type="text" inputMode="decimal" value={wartosc} onChange={(e) => onZmien(e.target.value)} />
        <button type="button" className="iot-krokomierz__przycisk" aria-label={etykietaWiecej} onClick={onWiecej} disabled={wiecejWylaczone}><IkonaPlus /></button>
      </div>
      {jednostka && <span className="iot-kafel__jednostka" aria-hidden="true">{jednostka}</span>}
    </div>
  );
}

export interface PoleZdjecProps {
  etykieta: string;
  /** Typy plików, np. "image/*" */
  akceptuj?: string;
  wiele?: boolean;
  onWybierz: (pliki: FileList) => void;
  ikona: KomponentIkony;
  className?: string;
}

/** Pole do dodawania zdjęć - kliknij lub upuść. Pliki obsługuje aplikacja. */
export function PoleZdjec({ etykieta, akceptuj = "image/*", wiele, onWybierz, ikona: Ikona, className }: PoleZdjecProps) {
  return (
    <label className={cx("iot-upuszczanie", className)}>
      <Ikona />
      <span>{etykieta}</span>
      <input
        type="file"
        className="iot-upuszczanie__input"
        accept={akceptuj}
        multiple={wiele}
        onChange={(e) => { if (e.target.files && e.target.files.length > 0) onWybierz(e.target.files); }}
      />
    </label>
  );
}
